import React, { Fragment, useState, useEffect, useContext } from 'react';
import { Row, Col, Input, Spinner, Button, ButtonGroup, FormText, FormGroup, FormFeedback } from "reactstrap";
import useUser from '../../hooks/useUser';
import FormToast from '../Form/Toast';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { RolesConstants } from '../../constants/roles';
import './MyProfile.css';
import { UserContext } from '../../context/User';
import { PixelContext } from '../../context/Pixel';

function MyProfile() {
    const [accountAddress, setAccountAddress] = useState('');
    const [userData, setUserData] = useState(null);
    const [profileUpdated, setProfileUpdated] = useState(false);
    const [profileUpdating, setProfileUpdating] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleError, setRoleError] = useState(null);
    const [phone, setPhone] = useState('');
    const users = useUser();
    const navigate = useNavigate();
    const isAuthenticated = useIsAuthenticated();
    const { inProgress } = useMsal();
    const { roles, setUser } = useContext(UserContext);
    const { MetaPixel } = useContext(PixelContext);

    const fullName = userData ? (userData.firstName + ' ' + userData.lastName) : ''
    const email = userData ? userData.email : '';

    useEffect(() => {
        document.documentElement?.classList.add('overflow-hidden');
        document.getElementsByClassName('main-app-container')[0]?.classList.add('min-vh-100')

        return () => {
            document.getElementsByClassName('main-app-container')[0]?.classList.remove('min-vh-100');
            document.documentElement?.classList.remove('overflow-hidden');
        };
    });

    useEffect(() => {
		if (inProgress === InteractionStatus.None) {
			if (!isAuthenticated) {
				navigate('/auth/login', {state: {callbackUrl: window.location.pathname}});
			} else {
                users.getCurrentUser();
            }
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, inProgress]);

    useEffect(() => {
        let address = users.user?.accountAddress ? users.user?.accountAddress : '';
        let phone = users.user?.phone ? users.user?.phone : '';
        setAccountAddress(address);
        setPhone(phone);
        setUserData(users.user);
        if (users.user) {
            setUser(users.user);
            setSelectedRole(getUserRole(users.user));
        }

        if(profileUpdating) {
            setProfileUpdating(false);
            setProfileUpdated(true);
            MetaPixel.track('AddPaymentInfo');
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users.user, users.loading]);

    useEffect(() => {
        if (profileUpdating) {
            setProfileUpdated(false);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileUpdating]);

    useEffect(() => {
        if (roleError !== null && !users.loading) {
            setRoleError(
                !isAppUserRole(selectedRole)
            )
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole]);

    const isAppUserRole = (code) => {
        return (code === RolesConstants.HOMEOWNER
            || code === RolesConstants.CONTRACTOR);
    }

    const prepareRoles = () => {
        let newRoles = [selectedRole];

        // Return Ui selected role plus any non-app roles user may have(e.g. admin)
        roles.forEach((userRole) => {
            if (!isAppUserRole(userRole.code)) {
                newRoles.push(userRole.code);
            }
        });

        return newRoles;
    }


    const getUserRole = (user) => {
        let role = null;
        let countFound = 0;
        user?.roles?.forEach((userRole) => {
            if (isAppUserRole(userRole.code)) {
                countFound++;
                role = userRole.code;
            }
        });

        return countFound === 1 ? role : null;
    }

    const handleAccountAddressChange = (e) => {
        setAccountAddress(e.target.value);
    }

    const handlePhoneChange = (e) => {
        setPhone(e.target.value);
    }

    const handleSaveProfile = () => {
        if (accountAddress.length === 0) {
            alert("Account address cannot be empty !");
        } else {
            if (selectedRole !== RolesConstants.HOMEOWNER
                && selectedRole !== RolesConstants.CONTRACTOR
            ) {
                setRoleError(true);
                return;
            } else {
                setRoleError(false);
            }

            setProfileUpdating(true);
            let user = userData;
            if (user) {
                user.accountAddress = accountAddress;
                user.phone = phone;
                user.roles = prepareRoles();
                users.updateCurrentUser(user);
            } else {
                alert("An error occurred, please reload !");
            }
        }
    }

    return (
        <Fragment>
            <div className="profile-container">
                <div className="row header my-4">
                    <h1 className="fs-4">My Profile</h1>
                </div>
                <Row>
                    <Col md="3">
                        My name
                    </Col>
                    <Col md="5">
                        <div style={{width: '90%'}} className="d-inline-block">
                            <Input
                                disabled
                                type="text"
                                value={fullName}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className='mt-1'>
                    <Col md="3">
                        Email address
                    </Col>
                    <Col md="5">
                        <div style={{width: '90%'}} className="d-inline-block">
                            <Input
                                disabled
                                type="text"
                                value={email}
                            />
                        </div>
                    </Col>
                </Row>
                <Row className='mt-1'>
                    <Col md="3">
                        Phone
                    </Col>
                    <Col md="5">
                        <div style={{width: '90%'}} className="d-inline-block">
                            <Input
                                type="text"
                                value={phone}
                                onChange={handlePhoneChange}
                            />
                        </div>
                        <Spinner hidden={ !users.loading && userData !== null} className="ms-1" size="sm" />
                    </Col>
                </Row>
                <Row className='mt-1'>
                    <Col md="3">
                        My account address
                    </Col>
                    <Col md="5">
                        <div style={{width: '90%'}} className="d-inline-block">
                            <Input
                                type="text"
                                value={accountAddress}
                                onChange={handleAccountAddressChange}
                            />
                            <FormText className={ userData && !userData.accountAddress ? '' : 'd-none' }>
                                You dont have an account address yet! You can set one here
                            </FormText>
                        </div>
                        <Spinner hidden={ !users.loading && userData !== null} className="ms-1" size="sm" />
                    </Col>
                </Row>
                <Row className='mt-2'>
                    <Col md="3">
                        My Role
                    </Col>
                    <Col md="5">
                        <div style={{ width: '90%' }} className="d-inline-block">
                        <FormGroup>
                                <ButtonGroup invalid="invalid" className='w-100'>
                                    <Button
                                        className='w-50'
                                        color="primary"
                                        outline
                                        onClick={() => setSelectedRole(RolesConstants.HOMEOWNER)}
                                        active={selectedRole === RolesConstants.HOMEOWNER}
                                    >
                                        I'm a Homeowner
                                    </Button>
                                    <Button
                                        className='w-50'
                                        color="primary"
                                        outline
                                        onClick={() => setSelectedRole(RolesConstants.CONTRACTOR)}
                                        active={selectedRole === RolesConstants.CONTRACTOR}
                                    >
                                        I'm a Contractor
                                    </Button>
                                </ButtonGroup>
                                <FormFeedback className={ roleError ? 'd-block' : ''}>
                                    Please select a Role
                                </FormFeedback>
                            </FormGroup>
                        </div>
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col md="3">
                    </Col>
                    <Col md="5">
                        <div style={{width: '90%'}} className="d-inline-block text-end">
                            <Button color="success" disabled={users?.loading} onClick={handleSaveProfile}>
                                Save profile
                            </Button>
                        </div>
                        <Spinner hidden={ !users.loading || !userData } className="ms-1" size="sm" />
                    </Col>
                </Row>
                {
                    users.error &&
                    <div style={{position: 'absolute', top: 10, right: 10}}>
                        <FormToast
                            className="bg-danger"
                            dismissAfter="2"
                            show={true}
                            message="An error occurred obtaining or saving the profile. Please try again!"/>
                    </div>
                }
                {
                    users.user && profileUpdated &&
                    <div style={{position: 'absolute', top: 10, right: 10}}>
                        <FormToast
                            className="bg-success"
                            dismissAfter="2"
                            show={true}
                            message="Your profile was updated !"/>
                    </div>
                }
            </div>
        </Fragment>
    );
}

export default MyProfile;